import React, { useState, useEffect, useRef } from 'react';
import { 
  DollarSign, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  Receipt, 
  TrendingUp, 
  TrendingDown, 
  PieChart, 
  Filter,
  Search,
  Download,
  Upload,
  Target,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { 
  getBudgetData, 
  updateTotalBudget, 
  createBudgetCategory, 
  updateBudgetCategory, 
  deleteBudgetCategory,
  createBudgetExpense,
  updateBudgetExpense,
  deleteBudgetExpense,
  initializeDefaultCategories
} from '../lib/budget';
import { supabase } from '../lib/supabase';
import type { BudgetCategory, BudgetExpense, BudgetSummary, BudgetFilters } from '../types/budget';
import { budgetTips } from '../data/sampleBudget';

const BudgetPlanning = () => {
  const sparklesRef = useRef<HTMLDivElement>(null);
  const expensesListRef = useRef<HTMLDivElement>(null);
  const prevScrollTopRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const [totalBudget, setTotalBudget] = useState(0);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [expenses, setExpenses] = useState<BudgetExpense[]>([]);
  const [summary, setSummary] = useState<BudgetSummary>({
    totalBudget: 0,
    totalAllocated: 0,
    totalSpent: 0,
    remaining: 0,
    percentageSpent: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BudgetCategory | null>(null);
  const [editingExpense, setEditingExpense] = useState<BudgetExpense | null>(null);
  const [newBudget, setNewBudget] = useState('');
  const [addingExpense, setAddingExpense] = useState(false);
  const [filters, setFilters] = useState<BudgetFilters>({
    category: 'all',
    dateRange: {
      start: '',
      end: ''
    },
    minAmount: 0,
    maxAmount: 1000000
  });
  const [registries, setRegistries] = useState<{id: string; title: string; description: string | null}[]>([]);
  const [selectedRegistryId, setSelectedRegistryId] = useState<string>('');

  const [newCategory, setNewCategory] = useState({
    name: '',
    allocated: '' as string | number,
    color: '#3B82F6',
    icon_name: 'DollarSign',
    description: ''
  });

  const [newExpense, setNewExpense] = useState({
    id: '',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    notes: ''
  });

  useEffect(() => {
    const loadRegistries = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('registries')
        .select('id, title, description')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (data && data.length > 0) {
        setRegistries(data);
        setSelectedRegistryId(data[0].id);
      }
    };
    loadRegistries();
    
    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedRegistryId) {
      loadBudgetData();
    }
  }, [selectedRegistryId]);

  // Capture scroll position before re-render
  React.useLayoutEffect(() => {
    const currentRef = expensesListRef.current;
    if (currentRef) {
      prevScrollTopRef.current = currentRef.scrollTop;
      prevScrollHeightRef.current = currentRef.scrollHeight;
    }
  });

  // Restore scroll position after re-render when expenses data changes
  useEffect(() => {
    const currentRef = expensesListRef.current;
    if (currentRef && prevScrollHeightRef.current > 0) {
      // Calculate the relative scroll position as a fraction
      const scrollFraction = prevScrollTopRef.current / prevScrollHeightRef.current;
      // Apply this fraction to the new scroll height
      currentRef.scrollTop = scrollFraction * currentRef.scrollHeight;
    }
  }, [expenses]);

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Initialize default categories first
      await initializeDefaultCategories(selectedRegistryId || undefined);
      
      const data = await getBudgetData(selectedRegistryId || undefined);
      setTotalBudget(data.totalBudget);
      setCategories(data.categories);
      setExpenses(data.expenses);
      setSummary(data.summary);
    } catch (err) {
      console.error('Error loading budget data:', err);
      setError('Failed to load budget data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBudget = async () => {
    try {
      const budget = parseFloat(newBudget);
      if (isNaN(budget) || budget < 0) {
        setError('Please enter a valid budget amount');
        return;
      }

      // Check for database field overflow (max value: 99,999,999.99)
      if (budget >= 100000000) {
        setError('Budget amount is too large. Maximum allowed is LKR 99,999,999.99');
        return;
      }
      await updateTotalBudget(budget, selectedRegistryId || undefined);
      setTotalBudget(budget);
      setNewBudget('');
      loadBudgetData(); // Refresh to get updated summary
    } catch (err) {
      console.error('Error updating budget:', err);
      setError('Failed to update budget');
    }
  };

  const handleAddCategory = async () => {
    try {
      if (!newCategory.name.trim()) {
        setError('Category name is required');
        return;
      }

      await createBudgetCategory({
        ...newCategory,
        allocated: parseFloat(newCategory.allocated as string) || 0
      }, selectedRegistryId || undefined);
      setNewCategory({
        name: '',
        allocated: '',
        color: '#3B82F6',
        icon_name: 'DollarSign',
        description: ''
      });
      setShowAddCategory(false);
      loadBudgetData();
    } catch (err) {
      console.error('Error adding category:', err);
      setError('Failed to add category');
    }
  };

  const handleEditCategory = (category: BudgetCategory) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      allocated: category.allocated,
      color: category.color,
      icon_name: category.icon_name,
      description: category.description || ''
    });
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      await updateBudgetCategory(editingCategory.id, {
        ...newCategory,
        allocated: parseFloat(newCategory.allocated as string) || 0
      });
      setEditingCategory(null);
      setNewCategory({
        name: '',
        allocated: '',
        color: '#3B82F6',
        icon_name: 'DollarSign',
        description: ''
      });
      loadBudgetData();
    } catch (err) {
      console.error('Error updating category:', err);
      setError('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category? This will also delete all associated expenses.')) {
      return;
    }

    try {
      await deleteBudgetCategory(categoryId);
      loadBudgetData();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category');
    }
  };

  const handleAddExpense = async () => {
    try {
      setAddingExpense(true);
      if (!newExpense.category || !newExpense.description.trim() || !newExpense.amount) {
        setError('Please fill in all required fields');
        return;
      }

      const expenseToCreate = {
        category_id: newExpense.category, // Use the selected category ID
        amount: parseFloat(newExpense.amount),
        description: newExpense.description,
        date: newExpense.date,
        vendor: newExpense.vendor,
        notes: newExpense.notes,
      };

      await createBudgetExpense(expenseToCreate, selectedRegistryId || undefined);
      setNewExpense({
        id: '',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        notes: ''
      });
      setShowAddExpense(false);
      loadBudgetData();
    } catch (err) {
      console.error('Error adding expense:', err);
      setError('Failed to add expense');
    } finally {
      setAddingExpense(false);
    }
  };

  const handleEditExpense = (expense: BudgetExpense) => {
    setEditingExpense(expense);
    
    // Check if the expense's category still exists in the current categories list
    const categoryExists = categories.some(cat => cat.id === expense.category_id);
    
    setNewExpense({
      id: expense.id,
      category: categoryExists ? expense.category_id : '', // Reset to empty if category doesn't exist
      amount: expense.amount.toString(),
      description: expense.description,
      date: expense.date,
      vendor: expense.vendor || '',
      notes: expense.notes || '',
    });
    setShowAddExpense(true);
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense) return;

    try {
      if (!newExpense.category || !newExpense.description.trim() || !newExpense.amount) {
        setError('Please fill in all required fields');
        return;
      }

      const expenseToUpdate = {
        category_id: newExpense.category,
        amount: parseFloat(newExpense.amount),
        description: newExpense.description,
        date: newExpense.date,
        vendor: newExpense.vendor,
        notes: newExpense.notes,
      };

      await updateBudgetExpense(editingExpense.id, expenseToUpdate);
      setEditingExpense(null);
      setNewExpense({
        id: '',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        notes: ''
      });
      setShowAddExpense(false);
      loadBudgetData();
    } catch (err) {
      console.error('Error updating expense:', err);
      setError('Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await deleteBudgetExpense(expenseId);
      loadBudgetData();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Failed to delete expense');
    }
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setEditingExpense(null);
    setShowAddCategory(false);
    setShowAddExpense(false);
    setNewCategory({
      name: '',
      allocated: '',
      color: '#3B82F6',
      icon_name: 'DollarSign',
      description: ''
    });
    setNewExpense({
      id: '',
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      notes: ''
    });
    setError('');
  };

  // Filter expenses based on current filters
  const filteredExpenses = expenses.filter(expense => {
    const categoryMatch = filters.category === 'all' || expense.category_id === filters.category;
    const amountMatch = expense.amount >= filters.minAmount && expense.amount <= filters.maxAmount;
    
    let dateMatch = true;
    if (filters.dateRange.start && filters.dateRange.end) {
      const expenseDate = new Date(expense.date);
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      dateMatch = expenseDate >= startDate && expenseDate <= endDate;
    }
    
    return categoryMatch && amountMatch && dateMatch;
  });

  const getProgressColor = (percentage: number) => {
    if (percentage <= 50) return 'bg-green-500';
    if (percentage <= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const iconComponents = {
    DollarSign,
    Calendar,
    Receipt,
    Target,
    TrendingUp,
    TrendingDown,
    PieChart,
    Plus,
    Edit,
    Trash2
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-800">Budget Planning</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Track your wedding expenses and stay within budget
        </p>
        {registries.length > 1 && (
          <div className="flex justify-center">
            <div className="inline-flex items-center space-x-3 bg-white/70 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/50 shadow-sm">
              <Calendar className="w-5 h-5 text-pastel-purple-500" />
              <label className="text-sm font-medium text-gray-700">Event:</label>
              <select
                value={selectedRegistryId}
                onChange={(e) => setSelectedRegistryId(e.target.value)}
                className="px-3 py-1.5 bg-white/80 border border-gray-200 text-gray-800 rounded-lg focus:ring-2 focus:ring-pastel-purple-400 focus:border-pastel-purple-400 text-sm font-medium"
              >
                {registries.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-100/60 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Budget Overview */}
      <div className="card-pastel p-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Target className="w-6 h-6 text-pastel-blue-600" />
              <span className="text-gray-600">Total Budget</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">LKR {totalBudget.toLocaleString()}</p>
            <div className="mt-2">
              <input
                type="number"
                value={newBudget}
                onChange={(e) => setNewBudget(e.target.value)}
                placeholder="Update budget"
                className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-pastel-purple-400 focus:border-pastel-purple-400 text-sm"
              />
              <button
                onClick={handleUpdateBudget}
                className="w-full mt-2 px-3 py-1 btn-pastel text-sm"
              >
                Update
              </button>
            </div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <TrendingUp className="w-6 h-6 text-pastel-green-600" />
              <span className="text-gray-600">Allocated</span>
            </div>
            <p className="text-3xl font-bold text-pastel-green-600">LKR {summary.totalAllocated.toLocaleString()}</p>
            <p className="text-sm text-gray-500">
              {totalBudget > 0 ? ((summary.totalAllocated / totalBudget) * 100).toFixed(1) : 0}% of budget
            </p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Receipt className="w-6 h-6 text-red-500" />
              <span className="text-gray-600">Spent</span>
            </div>
            <p className="text-3xl font-bold text-red-500">LKR {summary.totalSpent.toLocaleString()}</p>
            <p className="text-sm text-gray-500">{summary.percentageSpent.toFixed(1)}% of budget</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <DollarSign className="w-6 h-6 text-yellow-500" />
              <span className="text-gray-600">Remaining</span>
            </div>
            <p className={`text-3xl font-bold ${summary.remaining >= 0 ? 'text-yellow-500' : 'text-red-500'}`}>
              LKR {summary.remaining.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">
              {totalBudget > 0 ? ((summary.remaining / totalBudget) * 100).toFixed(1) : 0}% remaining
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Budget Progress</span>
            <span>{summary.percentageSpent.toFixed(1)}% used</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full transition-all duration-300 ${getProgressColor(summary.percentageSpent)}`}
              style={{ width: `${Math.min(summary.percentageSpent, 100)}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Categories Section */}
        <div className="card-pastel p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Budget Categories</h2>
            <button
              onClick={() => setShowAddCategory(true)}
              className="flex items-center space-x-2 px-4 py-2 btn-pastel"
            >
              <Plus className="w-5 h-5" />
              <span>Add Category</span>
            </button>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-800 mb-2">No Categories Yet</h3>
              <p className="text-gray-500 mb-6">Create your first budget category to start tracking expenses</p>
              <button
                onClick={() => setShowAddCategory(true)}
                className="px-6 py-3 btn-pastel"
              >
                Create First Category
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {categories.map((category) => {
                const IconComponent = iconComponents[category.icon_name as keyof typeof iconComponents] || DollarSign;
                const spentPercentage = category.allocated > 0 ? (category.spent / category.allocated) * 100 : 0;
                
                return (
                  <div key={category.id} className="bg-white/60 p-4 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: category.color }}>
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">{category.name}</h3>
                          {category.description && (
                            <p className="text-xs text-gray-500">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="text-gray-500 hover:text-gray-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-gray-500">Allocated</p>
                        <p className="text-gray-800 font-medium">LKR {category.allocated.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Spent</p>
                        <p className="text-gray-800 font-medium">LKR {category.spent.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Remaining</p>
                        <p className={`font-medium ${category.allocated - category.spent >= 0 ? 'text-pastel-green-600' : 'text-red-500'}`}>
                          LKR {(category.allocated - category.spent).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Progress</span>
                        <span>{spentPercentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(spentPercentage)}`}
                          style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Expenses Section */}
        <div className="card-pastel p-8">
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">Expenses</h2>
              <button
                onClick={() => setShowAddExpense(true)}
                className="flex items-center space-x-2 px-4 py-2 btn-pastel"
              >
                <Plus className="w-5 h-5" />
                <span>Add Expense</span>
              </button>
            </div>
            {/* Filters */}
            <div className="flex items-center space-x-4">
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-pastel-purple-400 focus:border-pastel-purple-400"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-800 mb-2">No Expenses Yet</h3>
              <p className="text-gray-500 mb-6">Start tracking your wedding expenses</p>
              <button
                onClick={() => setShowAddExpense(true)}
                className="px-6 py-3 btn-pastel"
              >
                Add First Expense
              </button>
            </div>
          ) : (
            <div ref={expensesListRef} className="space-y-3 max-h-96 overflow-y-auto">
              {filteredExpenses.map((expense) => (
                <div key={expense.id} className="bg-white/60 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{expense.description}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{new Date(expense.date).toLocaleDateString()}</span>
                        <span>{categories.find(cat => cat.id === expense.category_id)?.name}</span>
                        {expense.vendor && <span>{expense.vendor}</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-800 font-medium">LKR {expense.amount.toLocaleString()}</span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="text-gray-500 hover:text-gray-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {expense.notes && (
                    <p className="text-sm text-gray-500 mt-2">{expense.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Budget Tips */}
      <div className="card-pastel p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Budget Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {budgetTips.map((tip, index) => (
            <div key={index} className="bg-white/60 p-4 rounded-lg">
              <h3 className="font-medium text-gray-800 mb-2">{tip.category}</h3>
              <p className="text-gray-600 text-sm">{tip.tip}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Category Modal */}
      {(showAddCategory || editingCategory) && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center p-4 z-50">
          <div className="card-pastel p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="input-pastel"
                  placeholder="e.g., Venue, Catering"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Allocated Amount (LKR)
                </label>
                <input
                  type="number"
                  value={newCategory.allocated === 0 ? '' : newCategory.allocated}
                  onChange={(e) => setNewCategory({ ...newCategory, allocated: e.target.value })}
                  className="input-pastel"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                  className="w-full h-10 rounded-md cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Description
                </label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="input-pastel"
                  rows={3}
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={editingCategory ? handleUpdateCategory : handleAddCategory}
                  className="btn-pastel"
                >
                  {editingCategory ? 'Update' : 'Add'} Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center p-4 z-50">
          <div className="card-pastel p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editingExpense ? 'Edit Expense' : 'Add Expense'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Category *
                </label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  className="input-pastel"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Description *
                </label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  className="input-pastel"
                  placeholder="What was this expense for?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Amount (LKR) *
                </label>
                <input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="input-pastel"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Vendor
                </label>
                <input
                  type="text"
                  value={newExpense.vendor}
                  onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })}
                  className="input-pastel"
                  placeholder="Who did you pay?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Notes
                </label>
                <textarea
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                  className="input-pastel"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={editingExpense ? handleUpdateExpense : handleAddExpense}
                  disabled={addingExpense}
                  className="btn-pastel disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingExpense ? 'Adding...' : editingExpense ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetPlanning;
